/**
 * The banner band's sign-in notices (docs/PLAN-signin-requirements-20261029.md, "The screens" 4; decision 3): the
 * countdown to a workspace's two-step requirement (SignInRequirementBanner.tsx) and the email notice (EmailVerifyBanner.tsx),
 * which carries the platform's dated email requirement when there is one. One table for both designs' bands, which draw
 * the notice each source registers; in a module of their own, as the other notices' are, so the band's own checks read
 * them.
 *
 * Safety copy (D9): a requirement's day, who requires it, and the one thing to do. Lowercase, as the band's notices are,
 * names and dates as written. A day is the doors' (noon UTC of it), named as the reader's language writes it, never
 * "in N days". Each stage has its phone line: one row on a 390px screen beside its action.
 */
import { dueDay, dueDayShort } from '../../lib/signInSecurity/signInDates';
import type { CountdownStage } from './signInRequirementBannerPolicy';

/** Who requires it: the workspaces by name ("Smith Law", "Smith Law and Acme", "Smith Law, Acme and Bell"). */
function requiredBy(workspaces: readonly string[]): string {
  if (workspaces.length === 0) return 'two-step sign-in is required';
  const names = workspaces.length === 1
    ? workspaces[0]
    : `${workspaces.slice(0, -1).join(', ')} and ${workspaces[workspaces.length - 1]}`;
  return `${names} ${workspaces.length === 1 ? 'requires' : 'require'} two-step sign-in`;
}

export const SIGN_IN_REQUIREMENT_COPY = {
  /** The countdown to two-step sign-in, by stage. */
  twoStep: {
    words: (stage: CountdownStage, workspaces: readonly string[], dueAtMs: number): string => {
      switch (stage) {
        case 'overdue': return `two-step sign-in was due by ${dueDay(dueAtMs)} — set it up now to keep using Prest0.`;
        case 'today': return `${requiredBy(workspaces)} by today, ${dueDay(dueAtMs)} — set it up now so you can keep signing in.`;
        default: return `${requiredBy(workspaces)} by ${dueDay(dueAtMs)} — set it up so you can keep signing in.`;
      }
    },
    /* The phone line: one row on a 390px screen, the action beside it. */
    wordsShort: (stage: CountdownStage, dueAtMs: number): string => {
      switch (stage) {
        case 'overdue': return 'two-step sign-in is overdue.';
        case 'today': return 'two-step sign-in is due today.';
        default: return `two-step sign-in due ${dueDayShort(dueAtMs)}.`;
      }
    },
    action: 'set it up',
  },
  /** The email notice: confirming the sign-in address, with the platform's date when it has set one. */
  email: {
    words: (email: string, dated: { stage: CountdownStage; dueAtMs: number } | null): string => {
      const link = `we sent a link to ${email}.`;
      const why = 'it proves the address is yours, and two-step sign-in needs it.';
      if (!dated) return `confirm your email — ${link} ${why}`;
      switch (dated.stage) {
        case 'overdue': return `confirm your email to keep using Prest0 — it was due by ${dueDay(dated.dueAtMs)}. ${link}`;
        case 'today': return `confirm your email today, ${dueDay(dated.dueAtMs)} — ${link}`;
        default: return `confirm your email by ${dueDay(dated.dueAtMs)} — ${link} ${why}`;
      }
    },
    /* The phone line (2026-09-13): undated, the address is what makes it actionable (you check THAT inbox), so it
       survives; dated, the day does, and the whole notice is one tap away. */
    wordsShort: (email: string, dated: { stage: CountdownStage; dueAtMs: number } | null): string => {
      if (!dated) return `confirm your email — check ${email}.`;
      switch (dated.stage) {
        case 'overdue': return 'confirm your email to keep using Prest0.';
        case 'today': return 'confirm your email today.';
        default: return `confirm your email by ${dueDayShort(dated.dueAtMs)}.`;
      }
    },
    resend: 'resend link',
    resendShort: 'resend',
    sending: 'sending…',
    sent: 'sent ✓',
  },
} as const;
